<template>
    <div class="dashboard-window" v-if="data && chartData">
        <div class="dashboard-window-header">
            <div class="window-header__title">
                Соотношение типов оплат
            </div>
        </div>

        <div class="dashboard-window-body">
            <div class="dashboard-window-body-item">
                <Doughnut
                    :options="chartSettings"
                    :data="chartData"
                />
            </div>
            <div class="dashboard-window-body-list">
                <div
                    class="dashboard-window-body-item"
                    v-for="(item, key) in getTop3"
                    :key="key"
                >
                    <p>{{ key }}</p>
                    <span>
                        <div class="circle" :style="{ backgroundColor: item.color}"></div>
                        <span>{{ formatNumber(item.percent) }} %</span>
                    </span>
                    <span>
                        <div class="circle" :style="{ backgroundColor: item.color}"></div>
                        <span>{{ formatNumber(item.revenue) }} ₽</span>
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { formatNumber } from '@/utils/numberFormat'
import { Doughnut } from 'vue-chartjs'
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend
} from 'chart.js'

ChartJS.register(ArcElement, Tooltip, Legend)

export default {
    props: {
        data: {
            type: Object,
            default: null
        }
    },

    components: {
        Doughnut
    },

    data() {
        return {
            chartData: null,

            chartSettings: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                const label = context.label
                                const value = context.raw

                                const percent = this.data?.[label]?.percent || 0

                                return `${label}: ${this.formatNumber(value)} ₽ (${percent}%)`
                            }
                        }
                    }
                }
            }
        }
    },

    computed: {
        getTop3() {
            if (!this.chartData || !this.data) return {}

            // Берём топ-3 элемента из отсортированных данных
            const top3Entries = Object.entries(this.data)
                .sort((a, b) => b[1].revenue - a[1].revenue)
                .slice(0, 3)

            // Создаём объект с данными и соответствующим цветом
            const top3 = {}
            top3Entries.forEach(([key, value]) => {
                const index = this.chartData.labels.indexOf(key)
                const color = this.chartData.datasets[0].backgroundColor[index] || '#000'
                top3[key] = {
                    ...value,   // revenue и percent
                    color       // добавляем цвет
                }
            })

            return top3
        }
    },

    methods: {
        formatNumber,

        initChart() {
            if (!this.data) return


            const chartData = {
                labels: [],
                datasets: [
                    {
                        data: [],
                        backgroundColor: []
                    }
                ]
            }

            // палитра цветов
            const colors = [
                '#B8E6FF', // голубой
                '#FFC3C7', // розовый
                '#C3FFD9', // мятный
                '#E5D4FF', // сиреневый
                '#FFE5B8', // бежевый
                '#D6F5F3', // светлая бирюза
                '#FFD9EC', // пастельный розовый
                '#E3F2FD', // холодный голубой
                '#F3E5F5', // мягкий фиолет
                '#FFF4CC', // кремовый
                '#D9FBEA'  // светлый зелёный
            ]

            // сортировка по убыванию выручки
            const sorted = Object.entries(this.data)
                .sort((a, b) => b[1].revenue - a[1].revenue)

            let index = 0

            for (const [key, item] of sorted) {
                chartData.labels.push(key)
                chartData.datasets[0].data.push(item.revenue)

                chartData.datasets[0].backgroundColor.push(
                    colors[index % colors.length]
                )

                index++
            }

            this.chartData = chartData
        }
    },

    watch: {
        data: {
            handler() {
                this.initChart()
            },
            deep: true,
            immediate: true
        }
    }
}
</script>

<style scoped lang="scss">
.dashboard-window {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 18px;
    height: 320px;
    font-family: 'SFProDisplay', sans-serif;
    background: var(--color-white);
    border-radius: 16px;
    border: 1px solid #F5F5F5;
    overflow: hidden;
    @media only screen and  (max-width: 425px) {
        height: 100%;
    }
    &-header {
        font-size: 16px;
        font-weight: 700;
    }

    &-body {
        position: relative;
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-gap: 14px;
        @media only screen and  (max-width: 425px) {
            grid-template-columns: 1fr;
        }
        &-item {
            padding: 10px 14px;
            background: #FAFAFA;
            border-radius: 16px;
            & canvas {
                height: 224px !important;
            }
        }
        &-list {
            display: flex;
            flex-direction: column;
            gap: 14px;
            & .dashboard-window-body-item {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                grid-gap: 10px;
                justify-items: start;
                height: 100%;
                & p {
                    font-weight: 500;
                    font-size: 16px;
                    grid-column: 1 / 3;
                    color: #464646;
                    @media only screen and  (max-width: 1440px) {
                        font-size: 14px;
                    }
                }
                & span {
                    padding-left: 10px;
                    padding-right: 6px;
                    display: flex;
                    align-items: center;
                    flex-wrap: nowrap;
                    gap: 6px;
                    white-space: nowrap;
                    height: 20px;
                    background: var(--color-white);
                    border-radius: 8px;
                    @media only screen and  (max-width: 1440px) {
                        font-size: 12px;
                    }
                    & .circle {
                        min-width: 10px;
                        min-height: 10px;
                        max-width: 10px;
                        max-height: 10px;
                        border-radius: 50%;
                    }
                }
            }
        }
    }
}
</style>
